/**
 * The "who to look for" half of the brief (Theresa, Oct 20 2026): every criterion the posting and
 * the kick-off sheet hold, each tagged with where it came from; must-haves that are alternatives
 * shown as one "at least N of these" group. The recruiter can remove, add, move a criterion between
 * must-have and nice-to-have, change a group's N or split a group up. The server re-checks every
 * edit (`cleanBrief`): a group that falls below two members dissolves there.
 */
import { useState } from 'react';
import type { BriefCriterion, BriefGroup, RoleBrief } from '../../types/taOutreach';

type Patch = Partial<Pick<RoleBrief, 'mustHave' | 'niceToHave' | 'groups'>>;

const SOURCE_TAG: Record<NonNullable<BriefCriterion['source']>, string> = {
  scorecard: 'Scorecard',
  kickoff: 'Kick-off',
  posting: 'Posting',
  recruiter: 'You',
};

const SOURCE_HINT: Record<NonNullable<BriefCriterion['source']>, string> = {
  scorecard: 'From the Qualifying column of the kick-off sheet’s scorecard',
  kickoff: 'From elsewhere in the kick-off sheet',
  posting: 'From the Lever posting',
  recruiter: 'Added by you',
};

function Chip({
  c,
  disabled,
  moveLabel,
  onMove,
  onRemove,
}: {
  c: BriefCriterion;
  disabled: boolean;
  moveLabel: string;
  onMove: () => void;
  onRemove: () => void;
}) {
  return (
    <li
      className="inline-flex items-center gap-1 rounded-full border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-2.5 py-1 text-[13px]"
      title={c.source ? SOURCE_HINT[c.source] : undefined}
    >
      {c.label}
      {c.source && <span className="text-[11px] text-[#979797]">· {SOURCE_TAG[c.source]}</span>}
      {!disabled && (
        <>
          <button type="button" aria-label={`${moveLabel}: ${c.label}`} title={moveLabel} className="text-[#707070] hover:text-[#0072EA]" onClick={onMove}>
            ⇅
          </button>
          <button type="button" aria-label={`Remove ${c.label}`} className="text-[#707070] hover:text-[#B3261E]" onClick={onRemove}>
            ×
          </button>
        </>
      )}
    </li>
  );
}

function AddInput({ onAdd }: { onAdd: (label: string) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const label = draft.trim();
    if (!label) return;
    onAdd(label);
    setDraft('');
  };
  return (
    <li>
      <input
        className="rounded-full border border-dashed border-[#C8C8C8] px-2.5 py-1 text-[13px] outline-none"
        placeholder="+ add"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
        onBlur={add}
      />
    </li>
  );
}

const added = (label: string): BriefCriterion => ({ id: 'new', label, kind: 'other', source: 'recruiter' });
const withoutGroup = ({ group: _group, ...c }: BriefCriterion): BriefCriterion => c;

export function BriefCriteria({ brief, disabled, onChange }: { brief: RoleBrief; disabled: boolean; onChange: (patch: Patch) => void }) {
  const must = brief.mustHave;
  const nice = brief.niceToHave;
  const groups = (brief.groups || []).filter((g) => must.some((c) => c.group === g.id));
  const loose = must.filter((c) => !groups.some((g) => g.id === c.group));

  const removeMust = (c: BriefCriterion) => onChange({ mustHave: must.filter((x) => x !== c) });
  const toNice = (c: BriefCriterion) => onChange({ mustHave: must.filter((x) => x !== c), niceToHave: [...nice, withoutGroup(c)] });
  const toMust = (c: BriefCriterion) => onChange({ niceToHave: nice.filter((x) => x !== c), mustHave: [...must, c] });
  const setMin = (g: BriefGroup, min: number) => onChange({ groups: groups.map((x) => (x.id === g.id ? { ...x, min } : x)) });
  const split = (g: BriefGroup) =>
    onChange({ mustHave: must.map((c) => (c.group === g.id ? withoutGroup(c) : c)), groups: groups.filter((x) => x.id !== g.id) });

  return (
    <div className="space-y-3">
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#707070]">Must-haves · the one question may only be about these</h4>
        <ul className="flex flex-wrap gap-1.5">
          {loose.map((c, i) => (
            <Chip key={`${c.id}-${i}`} c={c} disabled={disabled} moveLabel="Make it a nice-to-have" onMove={() => toNice(c)} onRemove={() => removeMust(c)} />
          ))}
          {!disabled && <AddInput onAdd={(label) => onChange({ mustHave: [...must, added(label)] })} />}
        </ul>
        {groups.map((g) => {
          const members = must.filter((c) => c.group === g.id);
          return (
            <div key={g.id} className="mt-2 rounded-md border border-dashed border-[#C8C8C8] px-3 py-2">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[#707070]">
                <span className="font-semibold text-[#1A1A1A]">{g.label}</span>
                <span>· enough when the profile shows at least</span>
                <select
                  aria-label={`How many of ${g.label}`}
                  className="h-6 rounded border border-solid border-[#E9EAEC] bg-white px-1"
                  value={g.min}
                  disabled={disabled}
                  onChange={(e) => setMin(g, Number(e.target.value))}
                >
                  {Array.from({ length: Math.max(1, members.length - 1) }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>of these</span>
                {!disabled && (
                  <button type="button" className="text-[#0072EA] underline" onClick={() => split(g)}>
                    Split up (each one required)
                  </button>
                )}
              </div>
              <ul className="flex flex-wrap gap-1.5">
                {members.map((c, i) => (
                  <Chip key={`${c.id}-${i}`} c={c} disabled={disabled} moveLabel="Make it a nice-to-have" onMove={() => toNice(c)} onRemove={() => removeMust(c)} />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#707070]">Nice to have</h4>
        <ul className="flex flex-wrap gap-1.5">
          {nice.map((c, i) => (
            <Chip
              key={`${c.id}-${i}`}
              c={c}
              disabled={disabled}
              moveLabel="Make it a must-have"
              onMove={() => toMust(c)}
              onRemove={() => onChange({ niceToHave: nice.filter((x) => x !== c) })}
            />
          ))}
          {!disabled && <AddInput onAdd={(label) => onChange({ niceToHave: [...nice, added(label)] })} />}
        </ul>
      </div>
    </div>
  );
}

/** The kick-off sheet's Sourcing Kit, kept for the search. Nothing when the sheet's kit is empty. */
export function SourcingKit({ kit }: { kit: RoleBrief['sourcingKit'] }) {
  const rows: [string, string[]][] = [
    ['Other titles', kit?.titleVariations || []],
    ['Companies', kit?.companies || []],
    ['Key words', kit?.keywords || []],
  ];
  if (!rows.some(([, items]) => items.length)) return null;
  return (
    <div className="mt-3 text-[13px]">
      <span className="block text-xs font-semibold text-[#707070]">From the kick-off sheet’s Sourcing Kit · used for the search</span>
      <dl className="mt-1 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
        {rows
          .filter(([, items]) => items.length)
          .map(([label, items]) => (
            <div key={label} className="contents">
              <dt className="text-[#707070]">{label}</dt>
              <dd className="m-0">{items.join(' · ')}</dd>
            </div>
          ))}
      </dl>
    </div>
  );
}
